import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Plus, Activity, AlertTriangle } from 'lucide-react';
import { getVitals, addVital, type IpdVital, type AddVitalPayload } from '@/lib/ipdApi';
import { format } from 'date-fns';

// ─── Abnormal value helpers ───────────────────────────────────────────────────

function bpClass(sys: number | null, dia: number | null): string {
  if (sys != null && sys > 140) return 'text-amber-600 font-medium';
  if (dia != null && dia > 90)  return 'text-amber-600 font-medium';
  return '';
}
function spo2Class(v: number | null): string {
  if (v != null && v < 95) return 'text-red-600 font-semibold';
  return '';
}
function tempClass(v: number | null): string {
  if (v != null && v > 38.5) return 'text-amber-600 font-medium';
  return '';
}
function pulseClass(v: number | null): string {
  if (v != null && (v > 100 || v < 60)) return 'text-amber-600 font-medium';
  return '';
}

function fmtBP(sys: number | null, dia: number | null): string {
  if (sys == null && dia == null) return '—';
  return `${sys ?? '?'}/${dia ?? '?'}`;
}
function fmtVal(v: number | null, unit = ''): string {
  if (v == null) return '—';
  return `${v}${unit}`;
}

// ─── Vitals Form ──────────────────────────────────────────────────────────────

interface VitalsFormState {
  recorded_at: string;
  bp_systolic: string;
  bp_diastolic: string;
  pulse: string;
  temp_celsius: string;
  spo2: string;
  respiratory_rate: string;
  weight_kg: string;
  urine_output_ml: string;
  fluid_intake_ml: string;
  notes: string;
}

const emptyForm = (): VitalsFormState => ({
  recorded_at: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
  bp_systolic: '', bp_diastolic: '',
  pulse: '', temp_celsius: '', spo2: '',
  respiratory_rate: '', weight_kg: '',
  urine_output_ml: '', fluid_intake_ml: '',
  notes: '',
});

function numOrNull(s: string): number | null {
  const n = parseFloat(s);
  return s.trim() === '' || isNaN(n) ? null : n;
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unit?: string;
  step?: string;
  placeholder?: string;
}

function VitalField({ label, value, onChange, unit, step = 'any', placeholder }: FieldProps) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">
        {label}{unit && <span className="text-muted-foreground/60"> ({unit})</span>}
      </label>
      <Input
        type="number"
        min="0"
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  admissionId: string;
  isActive?: boolean;
  admissionDate?: string | null;
  dischargeDate?: string | null;
}

export default function IPDVitalsTab({ admissionId, isActive = true, admissionDate, dischargeDate }: Props) {
  const minRecordedAt = admissionDate ? format(new Date(admissionDate), "yyyy-MM-dd'T'HH:mm") : undefined;
  // For active admissions maxRecordedAt is "now" — recomputed per-render so the
  // picker stays current as the user fills the form. For discharged admissions
  // it's fixed to the discharge timestamp.
  const maxRecordedAt = format(dischargeDate ? new Date(dischargeDate) : new Date(), "yyyy-MM-dd'T'HH:mm");
  const [vitals, setVitals]   = useState<IpdVital[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen]       = useState(false);
  const [form, setForm]       = useState<VitalsFormState>(emptyForm());
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVitals(admissionId);
      setVitals(data);
    } catch (e: any) {
      console.error('Failed to load vitals:', e);
    } finally {
      setLoading(false);
    }
  }, [admissionId]);

  useEffect(() => { load(); }, [load]);

  const set = (key: keyof VitalsFormState) => (v: string) =>
    setForm(f => ({ ...f, [key]: v }));

  const handleSave = async () => {
    setError('');

    // Validate recorded_at is within the admission window.
    // This catches manual keyboard input that bypasses the HTML min/max attributes.
    const nowStr = format(new Date(), "yyyy-MM-dd'T'HH:mm");
    const effectiveMax = dischargeDate
      ? format(new Date(dischargeDate), "yyyy-MM-dd'T'HH:mm")
      : nowStr;

    if (minRecordedAt && form.recorded_at < minRecordedAt) {
      setError('Recorded time cannot be before the admission date.');
      return;
    }
    if (form.recorded_at > effectiveMax) {
      setError(dischargeDate
        ? 'Recorded time cannot be after the discharge date.'
        : 'Recorded time cannot be in the future.');
      return;
    }

    const payload: AddVitalPayload = {
      recorded_at:     form.recorded_at || undefined,
      bp_systolic:     numOrNull(form.bp_systolic),
      bp_diastolic:    numOrNull(form.bp_diastolic),
      pulse:           numOrNull(form.pulse),
      temp_celsius:    numOrNull(form.temp_celsius),
      spo2:            numOrNull(form.spo2),
      respiratory_rate: numOrNull(form.respiratory_rate),
      weight_kg:       numOrNull(form.weight_kg),
      urine_output_ml: numOrNull(form.urine_output_ml),
      fluid_intake_ml: numOrNull(form.fluid_intake_ml),
      notes:           form.notes.trim() || undefined,
    };

    // At least one clinical value
    const clinical = [
      payload.bp_systolic, payload.bp_diastolic, payload.pulse,
      payload.temp_celsius, payload.spo2, payload.respiratory_rate,
      payload.weight_kg, payload.urine_output_ml, payload.fluid_intake_ml,
    ];
    if (clinical.every(v => v == null)) {
      setError('Enter at least one vital value.');
      return;
    }

    try {
      setSaving(true);
      await addVital(admissionId, payload);
      setOpen(false);
      setForm(emptyForm());
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save vitals.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Activity className="w-4 h-4" />
          <span>{vitals.length} {vitals.length === 1 ? 'entry' : 'entries'}</span>
        </div>
        {isActive && (
          <Button size="sm" onClick={() => { setForm(emptyForm()); setError(''); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Record Vitals
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : vitals.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">
          No vitals recorded yet.
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                {['Time', 'BP', 'Pulse', 'Temp', 'SpO2', 'RR', 'Weight', 'Urine', 'Fluid In', 'By'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vitals.map((v, i) => (
                <tr key={v.id} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                    {format(new Date(v.recorded_at), 'dd MMM, h:mm a')}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap', bpClass(v.bp_systolic, v.bp_diastolic))}>
                    {fmtBP(v.bp_systolic, v.bp_diastolic)}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap', pulseClass(v.pulse))}>
                    {fmtVal(v.pulse)}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap', tempClass(v.temp_celsius))}>
                    {fmtVal(v.temp_celsius, '°C')}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap', spo2Class(v.spo2))}>
                    {fmtVal(v.spo2, '%')}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtVal(v.respiratory_rate)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtVal(v.weight_kg, ' kg')}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtVal(v.urine_output_ml, ' ml')}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtVal(v.fluid_intake_ml, ' ml')}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                    {v.recorded_by_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Record Vitals Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Vitals</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Date/time */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Recorded At</label>
              <Input
                type="datetime-local"
                value={form.recorded_at}
                min={minRecordedAt}
                max={maxRecordedAt}
                onChange={e => set('recorded_at')(e.target.value)}
                className="h-8 text-sm"
              />
            </div>

            {/* BP — side by side */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Blood Pressure <span className="text-muted-foreground/60">(mmHg)</span>
              </label>
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="0" step="1"
                  placeholder="Sys"
                  value={form.bp_systolic}
                  onChange={e => set('bp_systolic')(e.target.value)}
                  className="h-8 w-20 text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-muted-foreground text-sm">/</span>
                <Input
                  type="number" min="0" step="1"
                  placeholder="Dia"
                  value={form.bp_diastolic}
                  onChange={e => set('bp_diastolic')(e.target.value)}
                  className="h-8 w-20 text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>

            {/* 2-column grid for the rest */}
            <div className="grid grid-cols-2 gap-3">
              <VitalField label="Pulse"            unit="bpm"  value={form.pulse}            onChange={set('pulse')}            step="1"   />
              <VitalField label="Temperature"      unit="°C"   value={form.temp_celsius}      onChange={set('temp_celsius')}     step="0.1" />
              <VitalField label="SpO2"             unit="%"    value={form.spo2}              onChange={set('spo2')}             step="1"   />
              <VitalField label="Respiratory Rate" unit="/min" value={form.respiratory_rate}  onChange={set('respiratory_rate')} step="1"   />
              <VitalField label="Weight"           unit="kg"   value={form.weight_kg}         onChange={set('weight_kg')}        step="0.1" />
              <VitalField label="Urine Output"     unit="ml"   value={form.urine_output_ml}   onChange={set('urine_output_ml')}  step="1"   />
              <VitalField label="Fluid Intake"     unit="ml"   value={form.fluid_intake_ml}   onChange={set('fluid_intake_ml')}  step="1"   />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input
                value={form.notes}
                onChange={e => set('notes')(e.target.value)}
                placeholder="Optional"
                className="h-8 text-sm"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
